<template>
    <CardFront customClasses="tw:max-w-md tw:w-full tw:my-6">
        <!-- Header -->
        <template #header>
            <div class="tw:flex tw:items-center tw:gap-2">
                <div class="tw:w-8 tw:h-8 tw:rounded-full tw:bg-emerald-100 tw:flex tw:items-center tw:justify-center">
                    <svg class="tw:w-4 tw:h-4 tw:text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18-3a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V9" />
                    </svg>
                </div>
                <h2 class="tw:text-base tw:font-semibold tw:text-slate-800 tw:m-0">
                    {{ $t('perfil.monedero_renovacion.titulo') }}
                </h2>
            </div>
        </template>

        <hr class="tw:border-slate-100 tw:mb-2" />

        <!-- Descuento ya aplicado -->
        <div v-if="descuento_aplicado > 0" class="tw:flex tw:flex-col tw:gap-4">
            <div class="tw:flex tw:items-center tw:gap-3 tw:bg-emerald-50 tw:border tw:border-emerald-200 tw:rounded-xl tw:p-4">
                <div class="tw:flex-shrink-0 tw:w-9 tw:h-9 tw:rounded-full tw:bg-emerald-100 tw:flex tw:items-center tw:justify-center">
                    <svg class="tw:w-5 tw:h-5 tw:text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div>
                    <p class="tw:text-sm tw:font-semibold tw:text-emerald-800 tw:m-0">
                        {{ $t('perfil.monedero_renovacion.descuento_aplicado') }}
                    </p>
                    <p class="tw:text-sm tw:text-emerald-600 tw:m-0">
                        {{ $t('perfil.monedero_renovacion.saldo_restante', { saldo: saldo_restante }) }}
                    </p>
                </div>
            </div>

            <Button :disabled="disabled" variant="danger" @click="cancelarDescuento">
                {{ $t('perfil.monedero_renovacion.cancelar_descuento') }}
            </Button>
        </div>

        <!-- Seleccionar descuento -->
        <div v-else class="tw:flex tw:flex-col tw:gap-4">

            <!-- Badge saldo -->
            <div class="tw:flex tw:items-center tw:gap-3 tw:bg-slate-50 tw:border tw:border-slate-200 tw:rounded-xl tw:px-4 tw:py-3">
                <div class="tw:flex-shrink-0 tw:w-9 tw:h-9 tw:rounded-full tw:bg-white tw:border tw:border-slate-200 tw:flex tw:items-center tw:justify-center">
                    <svg class="tw:w-4 tw:h-4 tw:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M21 12a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 12m18 0v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6m18 0V9M3 12V9m18-3a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 9m18 0V9" />
                    </svg>
                </div>
                <div>
                    <p class="tw:text-xs tw:text-slate-400 tw:m-0">{{ $t('perfil.monedero_renovacion.saldo_label') }}</p>
                    <p class="tw:text-base tw:font-semibold tw:text-slate-800 tw:m-0">
                        {{ $t('perfil.monedero_renovacion.saldo_disponible', { saldo: saldo_disponible }) }}
                    </p>
                </div>
            </div>

            <p class="tw:text-sm tw:text-slate-500 tw:m-0">
                {{ $t('perfil.monedero_renovacion.pregunta_descuento') }}
            </p>

            <!-- Pill límite -->
            <BadgeFront
                v-if="activar_limite_puntos && porcentaje_limite_puntos < 100"
                :label="$t('perfil.monedero_renovacion.limite_porcentaje', { porcentaje: porcentaje_limite_puntos })"
                variant="warning"
                :dot="false"
                custom-class="tw:self-start"
            />

            <hr class="tw:border-slate-100 tw:m-0" />

            <SliderInput
                id="saldo-monedero-range"
                v-model="descuento"
                :min="0"
                :max="maximo_descuento"
                :step="0.01"
                :label="$t('perfil.monedero_renovacion.selecciona_importe')"
                suffix="€"
            />

            <!-- Importe seleccionado -->
            <div class="tw:bg-slate-50 tw:border tw:border-slate-100 tw:rounded-xl tw:py-5 tw:flex tw:flex-col tw:items-center tw:gap-1">
                <p class="tw:text-xs tw:text-slate-400 tw:m-0 tw:uppercase tw:tracking-wide">
                    {{ $t('perfil.monedero_renovacion.descuento_seleccionado') }}
                </p>
                <div class="tw:flex tw:items-baseline tw:gap-1">
                    <span class="tw:text-5xl tw:font-bold tw:text-emerald-500 tw:tabular-nums">
                        {{ descuento }}
                    </span>
                    <span class="tw:text-2xl tw:font-medium tw:text-emerald-300">€</span>
                </div>
            </div>

            <Button
                variant="dark"
                :disabled="descuento <= 0"
                @click="confirmar"
            >
                {{ $t('perfil.monedero_renovacion.confirmar_descuento') }}
            </Button>
        </div>
    </CardFront>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import SliderInput from '@/modules/front/ui/SliderInput.vue';
    import Button from '@/modules/front/ui/Button.vue';

    const props = defineProps({
        saldo_disponible: { type: Number, required: true },
        importe: { type: Number, required: true },
        descuento_aplicado: { type: Number, required: true },
        activar_limite_puntos: { type: Number, default: 0 },
        porcentaje_limite_puntos: { type: Number, default: 100 },
        disabled: { type: Boolean, default: false }
    });

    const emit = defineEmits(['confirmado', 'cancelar']);

    const descuento = ref(0);

    const maximo_descuento = computed(() => {
        let max = props.importe < props.saldo_disponible ? props.importe : props.saldo_disponible;
        if (props.activar_limite_puntos && props.porcentaje_limite_puntos < 100) {
            const limite = props.saldo_disponible * (props.porcentaje_limite_puntos / 100);
            max = Math.min(max, limite);
        }
        return max;
    });

    const saldo_restante = computed(() => props.saldo_disponible - props.descuento_aplicado);

    function confirmar() {
        emit('confirmado', descuento.value);
    }

    function cancelarDescuento() {
        emit('cancelar');
    }
</script>
